import { useMemo } from 'react';
import type { ChannelSummary, PublicUser } from '@itsappy/shared';
import { useMemberUsers } from '@/hooks/queries';

/**
 * The person on the other end of a one-to-one DM, or null for anything else —
 * in a group there is no single "they" whose presence would mean anything.
 *
 * The peer is re-resolved against the member list rather than read straight
 * off the channel: `peers` is a snapshot taken when the channel list was
 * fetched, while the member list is what `presence:updated` writes into, so
 * only that one tracks whether they are online right now.
 */
export function useDmPeer(channel: ChannelSummary | undefined): PublicUser | null {
  const members = useMemberUsers();
  return useMemo(() => {
    if (channel?.kind !== 'dm' || channel.peers.length !== 1) return null;
    const peer = channel.peers[0];
    return members.find((member) => member.id === peer.id) ?? peer;
  }, [channel, members]);
}
